import { createEntityId } from '@/core/EntityId';
import type { Vec3 } from '@/flora/FloraInstance';
import {
  isValidRing,
  normalizeRing,
  orientedBox,
  rectangleRing,
  ringArea,
  ringBounds,
  ringContains,
  ringToWorld,
  toLocalPoint,
  type Point2,
  type Ring,
} from './SiteFootprint';

/**
 * Site objects — the things on a site that are neither ground nor plant (spec §14, extension).
 *
 * A landscape reconstruction is mostly negative space: a real garden is shaped by the house it sits
 * behind, the shed it works around, and the paving it is reached across. Until now the project
 * document had nowhere to put any of that, so a reconstructed site could only *imply* a building by
 * leaving a hole in the planting, and the capture manifest could not mention one at all — which is
 * why an AI-enhanced render invents its own house every time.
 *
 * Like flora, a site object is a **record, not a mesh**. A shed is a footprint, an eave height, a
 * roof shape and two colours; `SiteObjectGeometry` turns that into geometry deterministically. That
 * keeps the same properties the procedural flora system was built for: it round-trips through JSON,
 * it stays editable, it lights and shadows correctly under the live sun, and a vision model reading
 * a reference photograph can fill in these few parameters far more reliably than it can produce a
 * mesh. See `docs/SITE_FROM_REFERENCE.md`.
 *
 * ## Shape: rectangle, polygon, or massing
 *
 * Three levels of fidelity, each a superset of the one before, and every level optional so an
 * existing document keeps meaning exactly what it meant:
 *
 * 1. **Rectangle** — `widthMeters` × `depthMeters` about `position`, turned by `rotationY`. Still
 *    the default, and still what a simple shed or patio should use.
 * 2. **Polygon** — `footprint`, a closed ring in the object's local frame. Overrides the rectangle
 *    wherever it is present. This is what an L-shaped house, a curved driveway, or a kidney pool
 *    needs; approximating those with "the dominant rectangle" is how a reconstruction ends up with
 *    a garage sticking through a lawn.
 * 3. **Massing** — `masses`, several extruded volumes at different heights under one record, so a
 *    house plus its attached garage plus its porch is one object the user moves once. A pitched
 *    roof is solved *per mass*, which is exactly how real roofs work and is why massing and polygon
 *    footprints belong together: an L-shaped roof is two ridges, not one.
 *
 * `widthMeters` / `depthMeters` stay populated at every level — derived from the footprint's
 * oriented bounding box by {@link syncExtents} — because the inspector, the capture manifest, and
 * the site summary all want a number a person can read.
 */

/**
 * What a site object is.
 *
 * `building` and `paving` are parameterised: their shape is a consequence of dimensions the site
 * imposes, so a record generates them and generating them is strictly better than storing art.
 *
 * `prop` is the case that argument does not reach. A bench, a fire pit, a lamp post — their shape is
 * *identity*, not dimension. There is no honest parameterisation of "Adirondack chair"; a record
 * describing one in enough detail to generate it would be a mesh in a worse notation. So a prop
 * names an asset instead, via {@link SiteObject.modelId}, and the mesh lives in the library rather
 * than in the document.
 *
 * This is a smaller concession than it looks. The prop record still owns everything the other kinds
 * own — position, rotation, footprint, keep-out, lock and hide state — and still round-trips through
 * JSON as a handful of numbers and a string. What changed is only where the *geometry* comes from,
 * which is exactly the seam `FloraModelSource` already established for plants.
 */
export type SiteObjectKind = 'building' | 'paving' | 'prop';

export const SITE_OBJECT_KINDS: readonly SiteObjectKind[] = ['building', 'paving', 'prop'];

export type RoofShape = 'flat' | 'shed' | 'gable' | 'gambrel' | 'hip';

export const ROOF_SHAPES: readonly RoofShape[] = ['flat', 'shed', 'gable', 'gambrel', 'hip'];

/**
 * Surface treatments a flat site object can have. Drives colour today and the manifest's wording;
 * it is also the field a generated or photo-derived texture would hang off.
 *
 * `water` is included because a pool is a flat, hard-edged, plan-visible element that behaves
 * exactly like paving in this model — it occupies a footprint, refuses planting, and needs to be
 * named in the manifest. Calling it `concrete` and colouring it blue would put a lie in the prompt.
 */
export type PavingMaterial =
  | 'flagstone'
  | 'brick'
  | 'concrete'
  | 'gravel'
  | 'deck'
  | 'asphalt'
  | 'water';

export const PAVING_MATERIALS: readonly PavingMaterial[] = [
  'flagstone',
  'brick',
  'concrete',
  'gravel',
  'deck',
  'asphalt',
  'water',
];

/**
 * One extruded volume within a building.
 *
 * A mass carries its own roof because that is the unit a roof is actually solved over. Splitting an
 * L-shaped house into two masses gives it the two ridges it has in reality; asking one pitched roof
 * to span the L would roof the empty notch as well.
 */
export interface BuildingMass {
  /** Optional label, shown in the inspector — "main house", "garage", "porch". */
  name?: string;
  /** Outline in the object's local frame, metres. Wound counter-clockwise on the way in. */
  footprint: Ring;
  /** Wall height from this mass's own base to its eaves. */
  heightMeters: number;
  roof: RoofShape;
  /** Roof rise from eave to ridge. Ignored when `roof` is `flat`. */
  roofRiseMeters: number;
  /**
   * Lift of this mass's floor above the object's datum. A porch deck sits a step up; a walk-out
   * lower level sits below zero.
   */
  baseMeters?: number;
  /**
   * Turn the ridge across the mass's long axis instead of along it. Default is along, which is what
   * a roof does unless a wing was deliberately turned.
   */
  ridgeCrosswise?: boolean;
}

export interface SiteObject {
  id: string;
  kind: SiteObjectKind;
  /** Human name, shown in the inspector and used in the capture manifest. */
  name: string;
  /** Footprint centre in world meters. `y` is an offset above the sampled ground. */
  position: Vec3;
  /** Rotation about +Y in radians. The footprint is expressed in the rotated frame. */
  rotationY: number;
  /** Footprint extent along the object's local X, in meters. Derived when a footprint is present. */
  widthMeters: number;
  /** Footprint extent along the object's local Z, in meters. Derived when a footprint is present. */
  depthMeters: number;
  /** Buildings: wall height to the eaves. Paving ignores it. Ignored when `masses` is present. */
  heightMeters: number;
  /** Buildings: roof form. Paving ignores it. Ignored when `masses` is present. */
  roof: RoofShape;
  /** Buildings: roof rise from eave to ridge, in meters. Ignored when `masses` is present. */
  roofRiseMeters: number;
  /** Wall / siding colour as `#rrggbb`. For paving, the surface colour. */
  wallColor: string;
  /** Roof colour as `#rrggbb`. Paving ignores it. */
  roofColor: string;
  /** Paving: which surface this is. Buildings ignore it. */
  paving: PavingMaterial;
  /**
   * Extra clearance beyond the footprint where planting is refused, in meters. A house wants a
   * metre of access; a patio wants none, because a bed may legitimately run right up to the edge.
   */
  clearanceMeters: number;
  locked: boolean;
  hidden: boolean;
  /**
   * Polygon outline in the local frame. Replaces the width/depth rectangle when present, for both
   * geometry and keep-out. A building that also carries `masses` uses this only as its overall
   * outline for reporting.
   */
  footprint?: Ring;
  /** Buildings: several volumes under one record. When non-empty, these define the built form. */
  masses?: BuildingMass[];
  /**
   * Catalog id of an authored mesh to render instead of generated geometry — see
   * `SiteObjectModelSource`.
   *
   * Optional at every kind, not just `prop`: a shed that happens to have a good model should be able
   * to use it, and the fallback when the asset is absent is the procedural form it would otherwise
   * have had. An id naming a model that is missing, or still loading, is not an error — the object
   * renders procedurally and upgrades itself when the asset arrives.
   */
  modelId?: string;
}

/** Largest footprint dimension accepted, in meters — the site itself caps out at 200 m. */
export const MAX_SITE_OBJECT_METERS = 200;

export interface CreateSiteObjectOptions {
  kind?: SiteObjectKind;
  name?: string;
  position?: Vec3;
  rotationY?: number;
  widthMeters?: number;
  depthMeters?: number;
  /** Local-frame polygon outline. Takes precedence over width/depth. */
  footprint?: readonly Point2[];
  masses?: readonly BuildingMass[];
  /** Catalog id of an authored mesh. Blank or whitespace is treated as absent. */
  modelId?: string;
  overrides?: Partial<SiteObject>;
}

/** Sensible defaults per kind, so a new object is immediately something rather than a null shape. */
const DEFAULTS: Record<SiteObjectKind, Pick<SiteObject,
  'heightMeters' | 'roof' | 'roofRiseMeters' | 'wallColor' | 'roofColor' | 'paving' | 'clearanceMeters'
>> = {
  building: {
    heightMeters: 2.4,
    roof: 'gable',
    roofRiseMeters: 1.2,
    wallColor: '#b9b2a4',
    roofColor: '#6d6f73',
    paving: 'concrete',
    clearanceMeters: 0.8,
  },
  paving: {
    heightMeters: 0,
    roof: 'flat',
    roofRiseMeters: 0,
    wallColor: '#b0a293',
    roofColor: '#b0a293',
    paving: 'flagstone',
    clearanceMeters: 0,
  },
  prop: {
    // Roughly a garden bench, so a prop created before its catalog entry is read is still plausibly
    // sized rather than a 4 m cube. `PropLibrary.createProp` overwrites all three from the asset's
    // measured size, which is the path anything user-facing takes.
    heightMeters: 0.9,
    roof: 'flat',
    roofRiseMeters: 0,
    wallColor: '#8a7f6d',
    roofColor: '#4b4b4b',
    paving: 'concrete',
    // Zero, and deliberately so: planting is *supposed* to run right up to a bench or a boulder.
    // Giving props the access clearance a building needs would carve dead rings around every fixture.
    clearanceMeters: 0,
  },
};

/**
 * Default footprint per kind, in meters. Props are small, and inheriting the building default would
 * make every newly placed fixture start out the size of a shed.
 */
const DEFAULT_EXTENTS: Record<SiteObjectKind, { width: number; depth: number }> = {
  building: { width: 4, depth: 3 },
  paving: { width: 4, depth: 3 },
  prop: { width: 1.6, depth: 0.6 },
};

const KIND_NAMES: Record<SiteObjectKind, string> = {
  building: 'Building',
  paving: 'Paving',
  prop: 'Prop',
};

export function createSiteObject(options: CreateSiteObjectOptions = {}): SiteObject {
  const kind = options.kind ?? 'building';
  const extents = DEFAULT_EXTENTS[kind];
  const object: SiteObject = {
    id: createEntityId('site-'),
    kind,
    name: options.name ?? KIND_NAMES[kind],
    position: options.position ?? { x: 0, y: 0, z: 0 },
    rotationY: options.rotationY ?? 0,
    widthMeters: clampExtent(options.widthMeters ?? extents.width),
    depthMeters: clampExtent(options.depthMeters ?? extents.depth),
    ...DEFAULTS[kind],
    locked: false,
    hidden: false,
    ...options.overrides,
  };

  if (options.modelId !== undefined || options.overrides?.modelId !== undefined) {
    const modelId = (options.modelId ?? options.overrides?.modelId ?? '').trim();
    if (modelId.length > 0) object.modelId = modelId;
    else delete object.modelId;
  }

  const footprint = options.footprint ?? options.overrides?.footprint;
  if (isValidRing(footprint)) object.footprint = normalizeRing(footprint);

  const masses = options.masses ?? options.overrides?.masses;
  if (kind === 'building' && masses && masses.length > 0) {
    const usable = masses.filter((mass) => isValidRing(mass.footprint)).map(normalizeMass);
    if (usable.length > 0) object.masses = usable;
  }

  return syncExtents(object);
}

/** Copy a mass into canonical form — ring wound consistently, magnitudes clamped. */
export function normalizeMass(mass: BuildingMass): BuildingMass {
  const out: BuildingMass = {
    footprint: normalizeRing(mass.footprint),
    heightMeters: clamp(mass.heightMeters, 0, MAX_SITE_OBJECT_METERS),
    roof: ROOF_SHAPES.includes(mass.roof) ? mass.roof : 'gable',
    roofRiseMeters: clamp(mass.roofRiseMeters, 0, MAX_SITE_OBJECT_METERS),
  };
  if (mass.name !== undefined && mass.name.trim().length > 0) out.name = mass.name.trim();
  if (mass.baseMeters !== undefined && Number.isFinite(mass.baseMeters)) {
    out.baseMeters = clamp(mass.baseMeters, -MAX_SITE_OBJECT_METERS, MAX_SITE_OBJECT_METERS);
  }
  if (mass.ridgeCrosswise === true) out.ridgeCrosswise = true;
  return out;
}

/** Clamp a footprint dimension into the supported range. */
export function clampExtent(meters: number): number {
  if (!Number.isFinite(meters) || meters <= 0) return 0.1;
  return Math.min(MAX_SITE_OBJECT_METERS, meters);
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

/* --------------------------------- Shape resolution --------------------------------- */

/**
 * The local-frame rings this object actually occupies on the ground.
 *
 * One list rather than one ring, because a multi-mass building covers several. Every consumer that
 * cares about ground coverage — keep-out, ground sampling, the plan overlay — walks this, so
 * "what shape is this thing" is answered in exactly one place.
 */
export function footprintRings(object: SiteObject): Ring[] {
  if (object.kind === 'building' && object.masses && object.masses.length > 0) {
    return object.masses.map((mass) => mass.footprint);
  }
  if (isValidRing(object.footprint)) return [object.footprint];
  return [rectangleRing(object.widthMeters, object.depthMeters)];
}

/**
 * The object's overall outline in the local frame — its polygon if it has one, otherwise its
 * rectangle. Used for reporting and for the plan-view overlay, not for keep-out (which needs every
 * mass, not just the outline).
 */
export function outlineRing(object: SiteObject): Ring {
  if (isValidRing(object.footprint)) return object.footprint;
  const rings = footprintRings(object);
  if (rings.length === 1) return rings[0] as Ring;
  return rectangleRing(object.widthMeters, object.depthMeters);
}

/** The masses a building builds, synthesised from the flat fields when none were authored. */
export function resolveMasses(object: SiteObject): BuildingMass[] {
  if (object.kind !== 'building') return [];
  if (object.masses && object.masses.length > 0) return object.masses;
  return [
    {
      footprint: isValidRing(object.footprint)
        ? object.footprint
        : rectangleRing(object.widthMeters, object.depthMeters),
      heightMeters: object.heightMeters,
      roof: object.roof,
      roofRiseMeters: object.roofRiseMeters,
    },
  ];
}

/**
 * Refresh `widthMeters` / `depthMeters` from the footprint, so the readable extents never contradict
 * the actual shape. Mutates and returns the object — it is called on construction and after any
 * shape edit.
 */
export function syncExtents(object: SiteObject): SiteObject {
  const rings = footprintRings(object);
  if (rings.length === 1 && !isValidRing(object.footprint) && !object.masses) return object;

  const all = rings.flat();
  if (all.length < 3) return object;
  const box = orientedBox(all);
  object.widthMeters = clampExtent(box.halfWidth * 2);
  object.depthMeters = clampExtent(box.halfDepth * 2);
  return object;
}

/**
 * Total height a building occupies, eaves plus roof rise, over every mass. Used for bounding,
 * shadowing, and the manifest's description of how large the object reads.
 */
export function totalHeight(object: SiteObject): number {
  if (object.kind !== 'building') return 0;
  let max = 0;
  for (const mass of resolveMasses(object)) {
    const rise = mass.roof === 'flat' ? 0 : mass.roofRiseMeters;
    max = Math.max(max, (mass.baseMeters ?? 0) + mass.heightMeters + rise);
  }
  return max;
}

/* ------------------------------------ Keep-out ------------------------------------ */

/**
 * Whether a world-space point falls inside this object's footprint, expanded by its clearance plus
 * any extra margin (a plant's own footprint radius, typically).
 *
 * Pure, and the single definition of "blocked ground" — placement, the site generator's fidelity
 * checks, and the manifest all call this rather than each re-deriving a shape test.
 *
 * The query point is rotated into the object's frame by {@link toLocalPoint}, which is the true
 * inverse of the map the renderer uses. An earlier version inverted the rotation by negating the
 * angle inside the *forward* formula, which reapplies it — so a rotated, non-square building's
 * keep-out sat at `−rotationY` and every plant placed against its walls landed inside them.
 */
export function footprintContains(
  object: SiteObject,
  x: number,
  z: number,
  extraMargin = 0,
): boolean {
  const margin = Math.max(0, object.clearanceMeters) + Math.max(0, extraMargin);
  const local = toLocalPoint({ x, z }, object.position, object.rotationY);
  return footprintRings(object).some((ring) => ringContains(ring, local.x, local.z, margin));
}

/** The first object blocking this point, or undefined when the ground is clear. */
export function blockingObject(
  objects: readonly SiteObject[],
  x: number,
  z: number,
  extraMargin = 0,
): SiteObject | undefined {
  return objects.find(
    (object) => !object.hidden && footprintContains(object, x, z, extraMargin),
  );
}

/**
 * The object's outline in world space, counter-clockwise. For a plain rectangle these are the four
 * corners it always were; for a polygon it is the whole ring.
 */
export function footprintCorners(object: SiteObject): Point2[] {
  return ringToWorld(outlineRing(object), object.position, object.rotationY);
}

/** Every ring this object covers, in world space — one per mass for a multi-mass building. */
export function footprintRingsWorld(object: SiteObject): Ring[] {
  return footprintRings(object).map((ring) =>
    ringToWorld(ring, object.position, object.rotationY),
  );
}

/**
 * Ground area in square meters, summed over the object's rings.
 *
 * Masses that overlap are counted twice. That is a deliberate simplification rather than a union
 * computation: a building's masses are authored to abut, and the number exists for a summary line,
 * not for a quantity survey.
 */
export function footprintArea(object: SiteObject): number {
  return footprintRings(object).reduce((total, ring) => total + ringArea(ring), 0);
}

/** Local-frame bounds across every ring — what the ground sampler walks to sit an object. */
export function footprintExtent(object: SiteObject): {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
} {
  return ringBounds(footprintRings(object).flat());
}
